import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../config/axios.config';
import UsageWidget from '../../components/subscription/UsageWidget';

/**
 * Subscription Dashboard Page
 * Displays tenant's current subscription details, features, and usage
 */
const SubscriptionDashboard = () => {
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [usageStats, setUsageStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch current subscription
            const subResponse = await axiosInstance.get('/subscription/current');

            if (subResponse.data.success && subResponse.data.data) {
                setSubscription(subResponse.data.data);
            } else {
                setSubscription(null);
            }

            // Fetch usage statistics
            try {
                const usageResponse = await axiosInstance.get('/subscription/usage-summary');
                if (usageResponse.data.success) {
                    setUsageStats(usageResponse.data.data);
                }
            } catch (usageError) {
                console.warn('Could not fetch usage stats:', usageError);
            }

        } catch (err) {
            console.error('Error fetching subscription:', err);
            setError(err.response?.data?.message || 'Failed to load subscription details');
        } finally {
            setLoading(false);
        }
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'cancelled':
                return 'warning';
            case 'expired':
                return 'danger';
            default:
                return 'info';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleViewPlans = () => {
        navigate('/app/subscription/plans');
    };

    if (loading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <Skeleton height="2rem" className="mb-3" />
                        <Skeleton height="5rem" className="mb-3" />
                        <Skeleton height="3rem" />
                    </Card>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <div className="text-center p-4">
                            <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '3rem' }}></i>
                            <h3 className="mt-3">Error Loading Subscription</h3>
                            <p className="text-600">{error}</p>
                            <Button label="Retry" icon="pi pi-refresh" onClick={fetchSubscriptionData} className="mt-3" />
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    if (!subscription) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <div className="text-center p-5">
                            <i className="pi pi-inbox text-600" style={{ fontSize: '4rem' }}></i>
                            <h2 className="mt-4 mb-2">No Active Subscription</h2>
                            <p className="text-600 mb-4">You don't have an active subscription plan yet.</p>
                            <Button
                                label="Browse Plans"
                                icon="pi pi-shopping-cart"
                                onClick={handleViewPlans}
                                className="p-button-lg"
                            />
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    const { plan } = subscription;

    return (
        <div className="grid">
            {/* Page Header */}
            <div className="col-12">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h2 className="m-0">Subscription Dashboard</h2>
                    <Button
                        label="Upgrade Plan"
                        icon="pi pi-arrow-up"
                        onClick={handleViewPlans}
                        className="p-button-success"
                    />
                </div>
            </div>

            {/* Current Plan Card */}
            <div className="col-12 lg:col-8">
                <Card className="mb-4">
                    <div className="flex justify-content-between align-items-start mb-4">
                        <div>
                            <h3 className="mt-0 mb-2">{plan.name}</h3>
                            <p className="text-600 mt-0">{plan.description || 'Your current subscription plan'}</p>
                        </div>
                        <Tag
                            value={subscription.status.toUpperCase()}
                            severity={getStatusSeverity(subscription.status)}
                            className="text-lg"
                        />
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="mb-3">
                                <span className="text-600">Plan Code</span>
                                <p className="font-semibold mt-1 mb-0">{plan.plan_code}</p>
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="mb-3">
                                <span className="text-600">Billing Interval</span>
                                <p className="font-semibold mt-1 mb-0">{subscription.billing_interval || 'Monthly'}</p>
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="mb-3">
                                <span className="text-600">Billing Cycle Start</span>
                                <p className="font-semibold mt-1 mb-0">{formatDate(subscription.billing_cycle_start)}</p>
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="mb-3">
                                <span className="text-600">Billing Cycle End</span>
                                <p className="font-semibold mt-1 mb-0">{formatDate(subscription.billing_cycle_end)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="border-top-1 border-200 pt-3 mt-3">
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <div className="text-center p-3 bg-blue-50 border-round">
                                    <i className="pi pi-wallet text-blue-500 mb-2" style={{ fontSize: '2rem' }}></i>
                                    <div className="text-600 text-sm">Credits Included</div>
                                    <div className="text-900 font-bold text-xl">{plan.credits_included?.toLocaleString() || 'Unlimited'}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="text-center p-3 bg-green-50 border-round">
                                    <i className="pi pi-star text-green-500 mb-2" style={{ fontSize: '2rem' }}></i>
                                    <div className="text-600 text-sm">Features</div>
                                    <div className="text-900 font-bold text-xl">{plan.feature_count || plan.features?.length || 0}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="text-center p-3 bg-purple-50 border-round">
                                    <i className="pi pi-users text-purple-500 mb-2" style={{ fontSize: '2rem' }}></i>
                                    <div className="text-600 text-sm">Max Users</div>
                                    <div className="text-900 font-bold text-xl">{plan.max_users || 'Unlimited'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Features List */}
                <Card title="Included Features">
                    {plan.features && plan.features.length > 0 ? (
                        <div className="grid">
                            {plan.features.map((feature, index) => (
                                <div key={index} className="col-12 md:col-6">
                                    <div className="flex align-items-center p-2">
                                        <i className="pi pi-check-circle text-green-500 mr-2"></i>
                                        <div className="flex-1">
                                            <div className="font-medium">{feature.display_name}</div>
                                            {feature.is_unlimited ? (
                                                <div className="text-sm text-600">Unlimited usage</div>
                                            ) : (
                                                <div className="text-sm text-600">Up to {feature.usage_limit} per month</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-600">No features information available</p>
                    )}
                </Card>
            </div>

            {/* Usage Statistics Sidebar */}
            <div className="col-12 lg:col-4">
                <UsageWidget usageStats={usageStats} />

                <Card title="Quick Actions" className="mt-4">
                    <div className="flex flex-column gap-2">
                        <Button
                            label="View All Plans"
                            icon="pi pi-th-large"
                            className="p-button-outlined w-full"
                            onClick={handleViewPlans}
                        />
                        <Button
                            label="Billing History"
                            icon="pi pi-file"
                            className="p-button-outlined w-full"
                            onClick={() => navigate('/app/billing')}
                        />
                        <Button
                            label="Download Invoice"
                            icon="pi pi-download"
                            className="p-button-outlined w-full"
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default SubscriptionDashboard;
